import React, { useState } from 'react';
import type { JSX } from 'react';

import classNames from 'classnames';

import styles from './CustomSwitch.module.css';

export interface CustomSwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'large' | 'medium' | 'small';
  color?: 'danger' | 'primary' | 'success' | 'warning';
  label?: string;
  /** Keep `label` as the accessible name only, without printing it beside the switch. */
  hideLabel?: boolean;
  id?: string;
  className?: string;
}

export function CustomSwitch({
  checked = false,
  onChange,
  disabled = false,
  size = 'medium',
  color = 'primary',
  label,
  hideLabel = false,
  id,
  className = '',
}: CustomSwitchProps): JSX.Element {
  const [internalChecked, setInternalChecked] = useState(checked);

  // Use controlled or uncontrolled mode
  const isChecked = onChange ? checked : internalChecked;

  const handleToggle = () => {
    if (disabled) return;

    const newChecked = !isChecked;

    if (onChange) {
      onChange(newChecked);
    } else {
      setInternalChecked(newChecked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggle();
    }
  };

  const switchClasses = classNames(
    styles.Switch,
    styles[size],
    styles[color],
    isChecked ? styles.Checked : styles.Unchecked,
    disabled && styles.Disabled,
    className,
  );

  const thumbClasses = classNames(
    styles.Thumb,
    styles[`thumb${size.charAt(0).toUpperCase()}${size.slice(1)}`],
    isChecked ? styles.ThumbChecked : styles.ThumbUnchecked,
  );

  return (
    <div className={styles.Container}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-disabled={disabled}
        aria-label={label}
        className={switchClasses}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        <span className={thumbClasses} />
      </button>
      {label && !hideLabel && (
        <label
          htmlFor={id}
          className={classNames(styles.Label, {
            [styles.LabelDisabled]: disabled,
          })}
        >
          {label}
        </label>
      )}
    </div>
  );
}

export default CustomSwitch;
